import { motion } from 'framer-motion';
import { FileText, Calendar } from 'lucide-react';

export function TermsOfService() {
  const lastUpdated = "March 14, 2026";

  return (
    <div className="min-h-screen bg-background">
      <main className="pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Header */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-50 mb-6">
                <FileText className="w-8 h-8 text-blue-600" />
              </div>
              <h1 className="text-4xl font-bold text-foreground mb-4">
                Terms of Service
              </h1>
              <div className="flex items-center justify-center gap-2 text-muted-foreground">
                <Calendar className="w-4 h-4" />
                <span>Last updated: {lastUpdated}</span>
              </div>
            </div>

            {/* Content */}
            <div className="prose prose-slate max-w-none">
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-foreground mb-4">1. Agreement to Terms</h2>
                <p className="text-muted-foreground leading-relaxed">
                  By accessing or using RaiseBeacon ("we," "our," or "us"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of the terms, you may not access the Service.
                </p>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  These Terms apply to all visitors, users, and others who access or use the Service. By accessing or using the Service, you acknowledge that you have read, understood, and agree to be bound by these Terms.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-foreground mb-4">2. Description of Service</h2>
                <p className="text-muted-foreground leading-relaxed">
                  RaiseBeacon provides a subscription-based investor database service that allows users to access information about investors, venture capital firms, and funding sources (the "Service"). The Service includes but is not limited to:
                </p>
                <ul className="list-disc pl-6 mt-3 space-y-2 text-muted-foreground">
                  <li>Access to investor profiles and contact information</li>
                  <li>Search and filtering capabilities</li>
                  <li>Export functionality for subscribed users</li>
                  <li>Educational resources and tutorials</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-foreground mb-4">3. User Accounts</h2>
                
                <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Account Creation</h3>
                <p className="text-muted-foreground leading-relaxed">
                  To access certain features of the Service, you must create an account. You agree to provide accurate, current, and complete information during the registration process and to update such information to keep it accurate, current, and complete.
                </p>

                <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Account Security</h3>
                <p className="text-muted-foreground leading-relaxed">
                  You are responsible for safeguarding the password that you use to access the Service and for any activities or actions under your password. You agree not to disclose your password to any third party. You must notify us immediately upon becoming aware of any breach of security or unauthorized use of your account.
                </p>

                <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Account Termination</h3>
                <p className="text-muted-foreground leading-relaxed">
                  We may terminate or suspend your account immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach the Terms. Upon termination, your right to use the Service will immediately cease.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-foreground mb-4">4. Subscriptions and Payments</h2>
                
                <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Subscription Plans</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Access to certain features of the Service requires a paid subscription. We offer various subscription plans with different features and durations. Current pricing and features are available on our Pricing page.
                </p>

                <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Payment Terms</h3>
                <p className="text-muted-foreground leading-relaxed">
                  By subscribing to a paid plan, you agree to pay all fees associated with your subscription. All payments are processed through our third-party payment processors. You authorize us to charge your payment method for all fees due.
                </p>

                <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Refund Policy</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Due to the nature of digital content and immediate access to our database, all purchases are final and no refunds will be issued except where required by law. We may, at our sole discretion, offer refunds in exceptional circumstances.
                </p>

                <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Cancellation</h3>
                <p className="text-muted-foreground leading-relaxed">
                  You may cancel your subscription at any time. Your access to premium features will continue until the end of your current billing period. After cancellation, your account will have limited access.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-foreground mb-4">5. Use of Data</h2>
                
                <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Permitted Use</h3>
                <p className="text-muted-foreground leading-relaxed">
                  The investor data provided through our Service is intended for legitimate business and fundraising purposes. You may use this data to:
                </p>
                <ul className="list-disc pl-6 mt-3 space-y-2 text-muted-foreground">
                  <li>Research potential investors for your business</li>
                  <li>Contact investors regarding investment opportunities</li>
                  <li>Build your professional network</li>
                  <li>Create internal reports and analyses</li>
                </ul>

                <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Prohibited Use</h3>
                <p className="text-muted-foreground leading-relaxed">
                  You may NOT use the Service or data for:
                </p>
                <ul className="list-disc pl-6 mt-3 space-y-2 text-muted-foreground">
                  <li>Spam, unsolicited marketing, or harassment</li>
                  <li>Reselling, redistributing, or sharing data with third parties</li>
                  <li>Scraping, automated data collection, or bulk downloading</li>
                  <li>Creating competing products or services</li>
                  <li>Any illegal or fraudulent purposes</li>
                  <li>Violating the privacy or rights of individuals</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-foreground mb-4">6. Intellectual Property</h2>
                <p className="text-muted-foreground leading-relaxed">
                  The Service and its original content, features, and functionality are and will remain the exclusive property of RaiseBeacon and its licensors. The Service is protected by copyright, trademark, and other laws of both the United States and foreign countries.
                </p>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  Our trademarks and trade dress may not be used in connection with any product or service without the prior written consent of RaiseBeacon. The investor data provided is compiled from public sources and is provided "as is" for your business use.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-foreground mb-4">7. Disclaimer of Warranties</h2>
                <p className="text-muted-foreground leading-relaxed">
                  THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS. BACKR MAKES NO REPRESENTATIONS OR WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, AS TO THE OPERATION OF THE SERVICE OR THE INFORMATION, CONTENT, MATERIALS, OR PRODUCTS INCLUDED ON THE SERVICE.
                </p>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  WE DO NOT GUARANTEE THAT:
                </p>
                <ul className="list-disc pl-6 mt-3 space-y-2 text-muted-foreground">
                  <li>The Service will be uninterrupted, timely, secure, or error-free</li>
                  <li>The results from using the Service will be accurate or reliable</li>
                  <li>Any errors in the Service will be corrected</li>
                  <li>Investors will respond to your outreach</li>
                  <li>Investor information is complete, accurate, or current</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-foreground mb-4">8. Limitation of Liability</h2>
                <p className="text-muted-foreground leading-relaxed">
                  IN NO EVENT SHALL BACKR, NOR ITS DIRECTORS, EMPLOYEES, PARTNERS, AGENTS, SUPPLIERS, OR AFFILIATES, BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM:
                </p>
                <ul className="list-disc pl-6 mt-3 space-y-2 text-muted-foreground">
                  <li>Your access to or use of or inability to access or use the Service</li>
                  <li>Any conduct or content of any third party on the Service</li>
                  <li>Any content obtained from the Service</li>
                  <li>Unauthorized access, use, or alteration of your transmissions or content</li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS ARISING FROM OR RELATING TO THESE TERMS OR YOUR USE OF THE SERVICE SHALL NOT EXCEED THE AMOUNT YOU PAID TO US DURING THE TWELVE (12) MONTH PERIOD IMMEDIATELY PRECEDING THE EVENT GIVING RISE TO LIABILITY.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-foreground mb-4">9. Indemnification</h2>
                <p className="text-muted-foreground leading-relaxed">
                  You agree to defend, indemnify, and hold harmless RaiseBeacon and its licensee and licensors, and their employees, contractors, agents, officers, and directors, from and against any and all claims, damages, obligations, losses, liabilities, costs or debt, and expenses (including but not limited to attorney's fees), resulting from or arising out of:
                </p>
                <ul className="list-disc pl-6 mt-3 space-y-2 text-muted-foreground">
                  <li>Your use and access of the Service</li>
                  <li>Your violation of any term of these Terms</li>
                  <li>Your violation of any third-party right, including without limitation any copyright, property, or privacy right</li>
                  <li>Any claim that your use of the Service caused damage to a third party</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-foreground mb-4">10. Governing Law</h2>
                <p className="text-muted-foreground leading-relaxed">
                  These Terms shall be governed and construed in accordance with the laws of the United States, without regard to its conflict of law provisions. Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-foreground mb-4">11. Changes to Terms</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will try to provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.
                </p>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  By continuing to access or use our Service after those revisions become effective, you agree to be bound by the revised terms. If you do not agree to the new terms, please stop using the Service.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-foreground mb-4">12. Contact Us</h2>
                <p className="text-muted-foreground leading-relaxed">
                  If you have any questions about these Terms, please contact us:
                </p>
                <div className="mt-4 p-4 bg-muted rounded-lg">
                  <p className="text-foreground"><strong>Email:</strong> backrindex@gmail.com</p>
                  <p className="text-foreground mt-2"><strong>Business:</strong> RaiseBeacon</p>
                </div>
              </section>
            </div>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-muted text-muted-foreground py-12 border-t">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="text-foreground font-semibold">backr</span>
            </div>
            <p className="text-sm">© 2026 RaiseBeacon. All rights reserved.</p>
            <div className="flex items-center gap-6 text-sm">
              <a href="#/privacy" className="hover:text-foreground transition-colors">Privacy Policy</a>
              <a href="#/terms" className="hover:text-foreground transition-colors">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
